import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useNotificationStore = defineStore('notification', () => {
  // State
  const notifications = ref([]);
  const snackbar = ref({
    show: false,
    message: '',
    color: 'info',
    timeout: 5000
  });

  // Generate unique ID for notifications
  let notificationId = 0;
  function generateId() {
    return ++notificationId;
  }

  // Actions
  function showSuccess(message, timeout = 5000) {
    snackbar.value = {
      show: true,
      message,
      color: 'success',
      timeout
    };
  }

  function showError(message, timeout = 7000) {
    snackbar.value = {
      show: true,
      message,
      color: 'error',
      timeout
    };
  }

  function showWarning(message, timeout = 5000) {
    snackbar.value = {
      show: true,
      message,
      color: 'warning',
      timeout
    };
  }

  function showInfo(message, timeout = 5000) {
    snackbar.value = {
      show: true,
      message,
      color: 'info',
      timeout
    };
  }

  function hideSnackbar() {
    snackbar.value.show = false;
  }

  // Add persistent notification (for notification center if needed later)
  function addNotification(notification) {
    const id = generateId();
    notifications.value.unshift({
      id,
      timestamp: new Date(),
      read: false,
      ...notification
    });

    // Keep only last 50 notifications
    if (notifications.value.length > 50) {
      notifications.value = notifications.value.slice(0, 50);
    }

    return id;
  }

  function removeNotification(id) {
    const index = notifications.value.findIndex(n => n.id === id);
    if (index > -1) {
      notifications.value.splice(index, 1);
    }
  }

  function markAsRead(id) {
    const notification = notifications.value.find(n => n.id === id);
    if (notification) {
      notification.read = true;
    }
  }

  function clearAllNotifications() {
    notifications.value = [];
  }

  // Handle API errors with user-friendly messages
  function handleApiError(error, customMessage = null) {
    let message = customMessage || 'An unexpected error occurred';

    if (error.response) {
      // Server responded with an error
      const status = error.response.status;
      const serverMessage = error.response.data?.error || error.response.data?.message;

      switch (status) {
        case 400:
          message = serverMessage || 'Invalid request. Please check your input.';
          break;
        case 401:
          message = 'Session expired. Please log in again.';
          break;
        case 403:
          message = 'You do not have permission to perform this action.';
          break;
        case 404:
          message = serverMessage || 'The requested resource was not found.';
          break;
        case 409:
          message = serverMessage || 'A conflict occurred. The resource may already exist.';
          break;
        case 422:
          message = serverMessage || 'Validation failed. Please check your input.';
          break;
        case 429:
          message = 'Too many requests. Please wait a moment and try again.';
          break;
        case 500:
          message = 'Server error. Please try again later.';
          break;
        case 502:
        case 503:
        case 504:
          message = 'Service temporarily unavailable. Please try again later.';
          break;
        default:
          message = serverMessage || `Error: ${status}`;
      }
    } else if (error.request) {
      // Request was made but no response received
      message = 'Unable to connect to the server. Please check your internet connection.';
    } else if (error.message) {
      // Error in setting up the request
      message = error.message;
    }

    showError(message);

    // Also add to notification history
    addNotification({
      type: 'error',
      title: 'Error',
      message
    });

    return message;
  }

  return {
    // State
    notifications,
    snackbar,
    // Actions
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideSnackbar,
    addNotification,
    removeNotification,
    markAsRead,
    clearAllNotifications,
    handleApiError
  };
});
